import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Percent, Layers, Calendar, Sparkles, Star, ShieldCheck, Users, Wallet } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import CurrencySwitcher from "../components/CurrencySwitcher";
import { API } from "../lib/api";
import PartnerCheckoutModal from "../components/PartnerCheckoutModal";

const BUNDLES = [
  {
    tier: "petite",
    name: "Petite Pro Bundle",
    qty: 5,
    discount: 25,
    tag: "Starter",
    description: "Perfect for newer planners and stylists growing their list of bookings.",
    popular: false,
  },
  {
    tier: "classic",
    name: "Classic Pro Bundle",
    qty: 10,
    discount: 35,
    tag: "Most loved",
    description: "Our most popular bundle for active wedding & event vendors.",
    popular: true,
  },
  {
    tier: "grand",
    name: "Grand Pro Bundle",
    qty: 10,
    discount: 40,
    tag: "Studio favourite",
    description: "Built for studios and high-volume planners managing premium bookings.",
    popular: false,
  },
];

const VendorPartnersPage = () => {
  const { currency, format } = useCurrency();
  const [tiers, setTiers] = useState(null);
  const [bundles, setBundles] = useState(BUNDLES);
  const [checkoutBundle, setCheckoutBundle] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", email: "", category: "wedding-planner", bundle_id: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/payments/tiers?currency=${currency}`).then(r => setTiers(r.data)).catch(() => {});
  }, [currency]);

  useEffect(() => {
    axios.get(`${API}/partners/bundles?currency=${currency}`)
      .then(r => {
        if (r.data?.length) {
          // Normalize into the shape the tile renderer expects
          const normalized = r.data.map(b => ({
            id: b.id,
            tier: b.tier,
            name: b.name,
            qty: b.qty,
            discount: b.discount_pct,
            tag: b.tag || "",
            description: b.description || "",
            popular: !!b.popular,
            price: b.price,
            currency: b.currency,
          }));
          setBundles(normalized);
          // Default selected bundle in the form -> the popular one
          const pop = normalized.find(b => b.popular) || normalized[0];
          setForm(f => ({ ...f, bundle_id: f.bundle_id || pop?.id || "" }));
        }
      })
      .catch(() => {});
  }, [currency]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr(""); setSubmitting(true);
    try {
      await axios.post(`${API}/partners/applications`, {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim().toLowerCase(),
        category: form.category,
        bundle_id: form.bundle_id || null,
        message: form.message.trim() || "",
      });
      setSent(true);
      setTimeout(() => setSent(false), 6000);
      setForm({ ...form, name: "", company: "", email: "", message: "" });
    } catch (err) {
      setSubmitErr(err?.response?.data?.detail || "Could not submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-cream-100 via-cream-50 to-cream-100">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-coral-100/40 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-coral-200/30 blur-3xl" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 pt-16 pb-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-coral-300/15 text-coral-500 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
              <Star className="w-3.5 h-3.5" /> Vendor Partner Program
            </div>
            <h1 className="font-serif text-navy-700 text-4xl md:text-6xl leading-[1.05]">
              <span className="italic">Pro Bundles</span> for event vendors
            </h1>
            <p className="mt-6 text-lg text-navy-700/75 leading-relaxed">
              Pro Bundles allow you to purchase galleries upfront at an exclusive partner rate. You can buy in bulk and save up to <strong>40% off retail</strong>, giving you the flexibility to include galleries in your packages and either absorb the cost as a value-add or mark up to retail. You simply use your galleries whenever you need them — creating and assigning them to clients as bookings come in.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a href="#bundles" className="bg-coral-300 text-white px-7 py-3.5 rounded-full font-semibold hover:bg-coral-400 transition-colors soft-shadow-coral inline-flex items-center gap-2">
                See Pro Bundles <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#apply" className="border-2 border-navy-700 text-navy-700 px-7 py-3.5 rounded-full font-semibold hover:bg-navy-700 hover:text-white transition-colors">
                Apply to partner
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why partner */}
      <section className="section bg-cream-100">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-12">
            <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Why partner with us</p>
            <h2 className="font-serif text-navy-700 text-4xl md:text-5xl mt-3"><span className="italic">Bundle savings</span>, total flexibility</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Percent, t: "Save up to 40%", d: "Exclusive bulk pricing reserved for verified partners only." },
              { icon: Layers, t: "Use anytime", d: "Galleries don't expire until you assign them to a client booking." },
              { icon: Calendar, t: "Assign on demand", d: "Spin up a branded gallery the moment a booking is confirmed." },
              { icon: Wallet, t: "Mark up or absorb", d: "Include as a value-add or resell at full retail — your call." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-white rounded-2xl p-6 soft-shadow">
                <div className="w-11 h-11 rounded-xl bg-coral-300/20 text-coral-300 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                <h3 className="mt-4 font-serif italic text-xl text-navy-700">{t}</h3>
                <p className="mt-2 text-navy-700/70 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundles */}
      <section id="bundles" className="section bg-cream-200/60">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-10">
            <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Pro Bundles</p>
            <h2 className="font-serif text-navy-700 text-4xl md:text-5xl mt-3">Pick your <span className="italic">bundle</span></h2>
            <p className="mt-3 text-navy-700/70 max-w-xl mx-auto">Partner pricing is locked-in at purchase. Use your galleries any time, in any currency.</p>
            <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-full px-4 py-2 soft-shadow">
              <span className="text-xs uppercase tracking-wider text-navy-700/60 font-semibold">Currency</span>
              <CurrencySwitcher compact />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {bundles.map((b) => {
              const t = tiers?.[b.tier];
              const unitRetail = t?.price || 0;
              const totalRetail = unitRetail * b.qty;
              const partnerUnit = unitRetail * (1 - b.discount / 100);
              const totalPartner = partnerUnit * b.qty;
              const saved = totalRetail - totalPartner;
              return (
                <div key={b.id || b.tier} data-testid={`bundle-card-${b.tier}`} className={`relative rounded-2xl p-7 bg-white border ${b.popular ? "border-coral-300 soft-shadow-coral" : "border-cream-300/60 soft-shadow"}`}>
                  {b.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral-300 text-white text-[11px] font-bold tracking-wider uppercase px-3 py-1 rounded-full inline-flex items-center gap-1">
                      <Star className="w-3 h-3 fill-white" /> {b.tag}
                    </div>
                  )}
                  <div className="text-xs font-semibold uppercase tracking-wider text-coral-300">{b.qty} galleries</div>
                  <h3 className="font-serif italic text-2xl text-navy-700 mt-1">{b.name}</h3>
                  <p className="text-sm text-navy-700/60 mt-1.5">{b.description}</p>

                  <div className="mt-5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-serif text-navy-700">{format(partnerUnit)}</span>
                      <span className="text-sm text-navy-700/60">/ gallery</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <span className="line-through text-navy-700/40">{format(unitRetail)} retail</span>
                      <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[11px] font-bold">SAVE {b.discount}%</span>
                    </div>
                  </div>

                  <div className="mt-5 rounded-xl bg-cream-100 p-4 text-sm space-y-1.5">
                    <div className="flex justify-between"><span className="text-navy-700/70">Retail total</span><span className="line-through text-navy-700/40">{format(totalRetail)}</span></div>
                    <div className="flex justify-between"><span className="text-navy-700/70">Partner total</span><span className="font-semibold text-navy-700">{format(totalPartner)}</span></div>
                    <div className="flex justify-between pt-1.5 border-t border-cream-200"><span className="text-emerald-700 font-semibold">You save</span><span className="text-emerald-700 font-bold">{format(saved)}</span></div>
                  </div>

                  <ul className="mt-5 space-y-2">
                    {[
                      `${b.qty} ${b.tier} galleries to use any time`,
                      "Assign to clients as bookings come in",
                      "Brand each gallery your way",
                      "No expiry until assigned",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-navy-700/80">
                        <Check className="w-4 h-4 text-coral-300 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => setCheckoutBundle(b)}
                    data-testid={`bundle-buy-${b.tier}`}
                    className={`mt-7 w-full text-center py-3 rounded-full font-semibold transition-colors ${b.popular ? "bg-coral-300 text-white hover:bg-coral-400" : "border-2 border-coral-300 text-coral-500 hover:bg-coral-300 hover:text-white"}`}
                  >
                    Pay &amp; activate now
                  </button>
                  <button
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, bundle_id: b.id })); document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' }); }}
                    data-testid={`bundle-request-${b.tier}`}
                    className="mt-2 w-full text-center py-2 rounded-full font-semibold text-navy-700/70 hover:text-navy-700 text-sm underline"
                  >
                    or apply for review
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-navy-700/50 mt-8">Pro Bundles are sold to verified vendor partners only. Custom volumes for studios &amp; agencies are available on request.</p>
        </div>
      </section>

      {/* How it works */}
      <section className="section bg-cream-100">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-12">
            <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">How it works</p>
            <h2 className="font-serif text-navy-700 text-4xl md:text-5xl mt-3">Three simple <span className="italic">steps</span></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { n: "01", t: "Apply", d: "Tell us about your business and which bundle fits your bookings." },
              { n: "02", t: "Buy upfront", d: "Pay once at partner pricing. Your bundle credits stay in your dashboard." },
              { n: "03", t: "Assign anytime", d: "Spin up a gallery for each client booking — and brand it your way." },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-7 soft-shadow">
                <div className="text-coral-300 font-serif text-5xl leading-none">{s.n}</div>
                <h3 className="mt-3 font-serif italic text-2xl text-navy-700">{s.t}</h3>
                <p className="mt-2 text-navy-700/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-20 bg-cream-200/60">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-serif text-navy-700 text-3xl md:text-4xl"><span className="italic">Built for</span> event professionals</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {["Wedding Planners", "Event Stylists", "Wedding Venues", "Photographers", "Videographers", "Coordinators", "Corporate Event Agencies", "Travel Concierge"].map(role => (
              <span key={role} className="bg-white border border-cream-300 text-navy-700 px-4 py-2 rounded-full text-sm font-medium soft-shadow">{role}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="section bg-cream-100">
        <div className="max-w-2xl mx-auto px-5 lg:px-10">
          <div className="text-center mb-8">
            <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Apply</p>
            <h2 className="font-serif text-navy-700 text-4xl md:text-5xl mt-3">Become a <span className="italic">partner</span></h2>
            <p className="mt-3 text-navy-700/70">Tell us a bit about your business — we&apos;ll get back within 1–2 business days.</p>
          </div>

          <form onSubmit={submit} className="bg-white rounded-3xl soft-shadow p-8 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Your name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-apply-name" className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Company</label>
                <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="partner-apply-company" className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Work email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="partner-apply-email" className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Business type</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 bg-white">
                  {["wedding-planner", "venue", "photographer", "videographer", "stylist", "agency", "other"].map(o => <option key={o} value={o}>{o.replace(/-/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Interested bundle</label>
                <select
                  value={form.bundle_id}
                  onChange={(e) => setForm({ ...form, bundle_id: e.target.value })}
                  data-testid="partner-apply-bundle"
                  className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 bg-white"
                >
                  {bundles.map(b => (
                    <option key={b.id || b.tier} value={b.id || ""}>{b.name} ({b.qty})</option>
                  ))}
                  <option value="">Custom / Volume</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Tell us about your events (optional)</label>
              <textarea rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How many events per year, average ticket size, etc." data-testid="partner-apply-message" className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
            </div>
            {sent && (
              <div data-testid="partner-apply-success" className="bg-emerald-50 text-emerald-700 rounded-md px-4 py-3 text-sm inline-flex items-center gap-2">
                <Check className="w-4 h-4" /> Thanks! Your application was received. We&apos;ll be in touch within 1–2 business days.
              </div>
            )}
            {submitErr && (
              <div data-testid="partner-apply-error" className="bg-red-50 text-red-700 rounded-md px-4 py-3 text-sm">{submitErr}</div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="partner-apply-submit"
              className="w-full bg-coral-300 text-white py-3.5 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-60 inline-flex items-center justify-center gap-2 soft-shadow-coral"
            >
              {submitting ? "Sending…" : "Submit application"} <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-center text-navy-700/50">By applying you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-cream-200/40">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <h2 className="text-center font-serif text-navy-700 text-3xl md:text-4xl mb-10">Partner <span className="italic">FAQs</span></h2>
          <div className="space-y-3">
            {[
              { q: "Do galleries from my bundle expire?", a: "No. Your bundle credits stay in your dashboard until you assign them to a client event. The hosting period starts only when you activate a gallery." },
              { q: "Can I rebrand each gallery for my clients?", a: "Yes. Each Classic and Grand gallery can be customized with your client's name, theme colors, and a custom welcome message." },
              { q: "How do I get my partner discount?", a: "Submit an application above. Once approved, we'll send a private invoice link to purchase your bundle at the partner rate." },
              { q: "Can I top-up galleries mid-year?", a: "Yes — request any time. Your locked-in partner rate applies for the calendar year of your initial purchase." },
              { q: "Do you offer custom volumes?", a: "Absolutely. Studios and agencies running 50+ events / year qualify for volume rates beyond 40%. Reach out via the application form." },
            ].map((f) => (
              <details key={f.q} className="bg-white rounded-2xl border border-cream-200 px-5 py-4">
                <summary className="cursor-pointer font-semibold text-navy-700 list-none flex items-center justify-between">
                  {f.q}
                  <Sparkles className="w-4 h-4 text-coral-300" />
                </summary>
                <p className="mt-3 text-navy-700/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="section bg-cream-100">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-navy-700 to-navy-800 px-6 py-14 md:px-14 md:py-16 text-center">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-coral-300/30 blur-3xl" />
            <div className="absolute -left-10 bottom-0 w-72 h-72 rounded-full bg-coral-200/20 blur-3xl" />
            <div className="relative">
              <ShieldCheck className="w-10 h-10 text-coral-300 mx-auto" />
              <h2 className="font-serif text-cream-100 text-3xl md:text-4xl lg:text-5xl mt-4">Ready to bundle &amp; <span className="italic text-coral-300">save?</span></h2>
              <p className="text-cream-100/75 mt-3 max-w-xl mx-auto">Join the vendor partner program and offer your clients a private, branded gallery experience without compressing your margins.</p>
              <a href="#apply" className="mt-6 inline-flex items-center gap-2 bg-coral-300 text-white px-7 py-3.5 rounded-full font-semibold hover:bg-coral-400">
                Apply now <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {checkoutBundle && (
        <PartnerCheckoutModal
          bundle={checkoutBundle}
          onClose={() => setCheckoutBundle(null)}
        />
      )}
    </>
  );
};

export default VendorPartnersPage;
